import React from 'react';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Zap, TrendingUp } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

/**
 * UsageMeter shows monthly AI analysis usage as a progress bar.
 * Variants:
 *  - 'compact'  : tiny inline meter (for headers/topbars)
 *  - 'default'  : card-sized meter (for dashboard sidebars)
 *  - 'full'     : large card with upgrade CTA (for settings/dashboard hero)
 */
const UsageMeter = ({ variant = 'default', className = '' }) => {
  const navigate = useNavigate();
  const { usage } = useWorkspace();

  if (!usage) return null;

  const used = usage.total || 0;
  const limit = usage.limit || 1;
  const remaining = usage.remaining ?? Math.max(0, limit - used);
  const planName = usage.plan || 'Demo';
  const percent = Math.min(100, Math.round((used / limit) * 100));

  // Color thresholds
  const isOver = used >= limit;
  const isCritical = percent >= 90;
  const isWarning = percent >= 75;

  let barColorClass = 'bg-primary';
  if (isOver) barColorClass = 'bg-destructive';
  else if (isCritical) barColorClass = 'bg-destructive';
  else if (isWarning) barColorClass = 'bg-orange-500';

  // Compact variant: tiny inline pill
  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={() => navigate('/pricing')}
        className={`group flex items-center gap-2 px-3 py-1.5 rounded-md border bg-card hover:bg-muted transition-colors ${className}`}
        data-testid="usage-meter-compact"
        title={`${used} / ${limit} AI analyses used this month`}
      >
        <Zap className={`h-3.5 w-3.5 ${isWarning ? 'text-orange-500' : 'text-primary'}`} />
        <div className="flex flex-col items-start min-w-[80px]">
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <span data-testid="usage-meter-text">{used}/{limit}</span>
            <span className="text-muted-foreground">analyses</span>
          </div>
          <div className="w-full h-1 rounded-full bg-muted overflow-hidden mt-0.5">
            <div
              className={`h-full rounded-full transition-all ${barColorClass}`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
          {planName}
        </Badge>
      </button>
    );
  }

  // Full variant: large card with upgrade CTA
  if (variant === 'full') {
    return (
      <div
        className={`rounded-xl border bg-card p-6 ${className}`}
        data-testid="usage-meter-full"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold">Monthly AI analyses</h3>
              <Badge variant="secondary" className="text-xs">{planName} plan</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Resets on the 1st of every month
            </p>
          </div>
          {isWarning && (
            <Badge
              variant={isOver ? 'destructive' : 'outline'}
              className={`flex items-center gap-1 ${isOver ? '' : 'border-orange-500 text-orange-600'}`}
            >
              <AlertTriangle className="h-3 w-3" />
              {isOver ? 'Limit reached' : 'Running low'}
            </Badge>
          )}
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-baseline justify-between">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold tabular-nums" data-testid="usage-meter-used">
                {used}
              </span>
              <span className="text-lg text-muted-foreground">/ {limit}</span>
            </div>
            <span className="text-sm text-muted-foreground tabular-nums">
              {remaining} remaining
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${barColorClass}`}
              style={{ width: `${percent}%` }}
              data-testid="usage-meter-bar"
            />
          </div>
        </div>

        {(isWarning || isOver) && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-3 border-t">
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">
                {isOver
                  ? 'Upgrade to run more analyses this month'
                  : 'Upgrade for more headroom'}
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/pricing')}
              className="btn-cta sm:ml-auto"
              data-testid="usage-meter-upgrade-button"
            >
              View plans
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Default variant: sidebar card
  return (
    <div
      className={`rounded-xl border bg-card p-4 ${className}`}
      data-testid="usage-meter-default"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">AI analyses</span>
        </div>
        <Badge variant="outline" className="text-xs">{planName}</Badge>
      </div>

      <div className="flex items-baseline justify-between mb-2">
        <div>
          <span className="text-2xl font-bold tabular-nums">{used}</span>
          <span className="text-sm text-muted-foreground"> / {limit}</span>
        </div>
        <span className="text-xs text-muted-foreground">{percent}% used</span>
      </div>

      <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColorClass}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {isWarning && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/pricing')}
          className="w-full mt-3"
          data-testid="usage-meter-upgrade-link"
        >
          {isOver ? 'Upgrade plan' : 'See plans'}
        </Button>
      )}
    </div>
  );
};

export default UsageMeter;
